/**
 *
 * Copyright (C) SiteVision AB 2002-2023, all rights reserved
 *
 * @author Henrik Ekelöf
 *
 * SiteVision Responsive Tables
 *
 * You may override the breakpoint for a single page or for the website
 * by setting the breakpoints var [string]
 *
 * <script>
 *	  window.sv.responsiveTables = { config: { breakpoint : '40em' } };
 * </script>
 *
 * It's also possible to change the width of the first column in
 * Horizontal scroll with fixed first column
 * by setting fixedFirstWidth [string]
 *
 * <script>
 *	  window.sv.responsiveTables = { config: { fixedFirstWidth : '30%' } };
 * </script>
 *
 * Here's both custom in one:
 *
 * <script>
 *	  window.sv.responsiveTables = { config: { breakpoint : '40em', fixedFirstWidth : '30%' } };
 * </script>
 */

/* SiteVision Responsive Tables Config */

import $ from '@sv/jquery';
import sv from '@sv/core';
import { TemplateUtil as templateUtil } from '@sv/util';

const baseClassName = 'sv-responsiveTable';
const responsiveTables = sv.responsiveTables || {};

responsiveTables.config = responsiveTables.config || {};
responsiveTables.classNames = {};
responsiveTables.selectors = {};

$.each(
  [
    ['groupName', ''],
    ['scroll', '--scroll'],
    ['fixedFirst', '--fixedFirst'],
    ['stackGroupName', '--stacked'],
    ['stackTable', '--stackTable'],
    ['stackColumns', '--stackColumns'],
  ],
  function (i, t) {
    responsiveTables.classNames[t[0]] = baseClassName + t[1];
    responsiveTables.selectors[t[0]] = '.' + baseClassName + t[1];
  }
);

const defaultConfig = {
  breakpoint: '767px',
  fixedFirstWidth: '35%',
  // HTML Templates - probably no need to modify, but anyway ...
  divClassTmpl: '<div class="<%= classNames %>" />',
  divStyleTmpl: '<div class="<%= classNames %>" style="<%= styleAttr %>" />',
  tblTmpl: '<table class="<%= classNames %>"><tbody></tbody></table>',
  tblThRowTmpl:
    '<tr class="<%= trClassNames %>">' +
    '<th class="<%= thClassNames %>" colspan="<%= colSpan %>">' +
    '<%= content %></th></tr>',
  tblTrRowTmpl:
    '<tr class="<%= trClassNames %>"><td class="<%= groupName %>__key">' +
    '<%= keyContent %></td><td class="<%= groupName %>__value <%= cellClassName %>">' +
    '<%= valueContent %></td></tr>',
};

responsiveTables.config = $.extend({}, defaultConfig, responsiveTables.config);

/**
 * SiteVision Responsive Tables
 */

const classNames = responsiveTables.classNames;
const selectors = responsiveTables.selectors;
const config = responsiveTables.config;

(function () {
  /**
   * Simple Scrolling Responsive Table
   */

  if (!$.svRespTables) {
    $.svRespTables = {};
  }

  $.svRespTables.scroll = function (el) {
    this.init = () => {
      $(el)
        .removeClass(classNames.groupName)
        .wrap(
          templateUtil.template(config.divClassTmpl, { classNames: classNames.groupName })
        );
    };
    this.init();
  };

  $.fn.svRespTables_scroll = function () {
    return this.each(function () {
      /*jshint nonew:false*/
      new $.svRespTables.scroll(this);
      /*jshint nonew:true*/
    });
  };

  /**
   * ZURB Studios Responsive Table
   * (Fixed first column)
   *
   * http://zurb.com/playground/responsive-tables
   *
   * MIT License
   *
   * modified by Henrik Ekelöf
   */

  $.svRespTables.fixedFirst = function (el) {
    var base = this,
      mql = responsiveTables.mql;

    const fullClassName = classNames.groupName + ' ' + classNames.fixedFirst;
    let switched = false;

    base.$el = $(el);

    base.$el.data('svRespTables.fixedFirst', base);

    base.init = function () {
      base.$el
        .removeClass(fullClassName)
        .wrap(templateUtil.template(config.divClassTmpl, { classNames: fullClassName }));
    };

    base.splitTable = function () {
      base.$copy = base.$el.clone();

      base.$copy
        .css('width', '100%')
        .find('td:not(:first-child), th:not(:first-child)')
        .hide()
        .end()
        .find('caption')
        .html('&nbsp;');

      base.$el.closest('.sv-responsiveTable--fixedFirst').append(base.$copy);

      base.$copy.wrap(
        templateUtil.template(config.divStyleTmpl, {
          classNames: classNames.fixedFirst + '__pinned',
          styleAttr: 'width: ' + config.fixedFirstWidth,
        })
      );

      base.$el.wrap(
        templateUtil.template(config.divStyleTmpl, {
          classNames: classNames.fixedFirst + '__scrollable',
          styleAttr: 'margin-left: ' + config.fixedFirstWidth,
        })
      );

      base.setCellHeights();
    };

    base.unSplitTable = function () {
      base.$el
        .closest(selectors.fixedFirst)
        .find(selectors.fixedFirst + '__pinned')
        .remove();
      base.$el.unwrap();
      base.$copy = undefined;
      base.unSetCellHeights();
    };

    base.setCellHeights = function () {
      var $tr = base.$el.find('tr'),
        $tr_copy = base.$copy.find('tr'),
        heights = [];

      $tr.each(function (i, el) {
        const $tx = $(el).find('th, td');

        $tx.each(function () {
          const height = $(this).outerHeight(true);

          heights[i] = heights[i] || 0;
          if (height > heights[i]) {
            heights[i] = height;
          }
        });
      });

      $tr_copy.each(function (i, el) {
        $(el).height(heights[i]);
        $tr.eq(i).height(heights[i]);
      });
    };

    base.unSetCellHeights = function () {
      base.$el.find('tr').each(function (i, el) {
        el.style.height = 'auto';
      });
    };

    base.updateTables = function () {
      if (!mql.matches && switched) {
        // Wider than config.breakpoint
        switched = false;
        base.unSplitTable();
      } else if (mql.matches && !switched) {
        switched = true;
        base.splitTable();
      }
    };

    mql.addListener(base.updateTables);
    $(base.updateTables);

    base.init();
  };

  $.fn.svRespTables_fixedFirst = function () {
    return this.each(function () {
      /*jshint nonew:false*/
      new $.svRespTables.fixedFirst(this);
      /*jshint nonew:true*/
    });
  };

  /**
   * stacktable.js
   * (Stacked table and stacked columns)
   *
   * Author & copyright (c) 2012: John Polacek
   * http://johnpolacek.github.io/stacktable.js/
   *
   * Dual MIT & GPL license
   *
   * modified by Henrik Ekelöf
   */

  //-Stacked Table-----------------------------------------------------------------------------//

  $.svRespTables.stackTable = function (el) {
    var base = this,
      mql = responsiveTables.mql;

    base.$el = $(el);

    base.$el.data('svRespTables.stackTable', base);

    base.init = function () {
      let markup = '';

      mql.removeListener(base.init);

      base.$copy = $(
        templateUtil.template(config.tblTmpl, {
          classNames:
            base.$el.prop('class') +
            ' ' +
            classNames.stackGroupName +
            '__smallOnly',
        })
      );

      base.$el.addClass(classNames.stackGroupName + '__largeOnly');

      const $caption = base.$el.find('caption').clone();
      const $topRow = base.$el.find('tr').eq(0);

      base.$el.find('tr').each(function (rowIndex, tr) {
        var $tr = $(tr);
        let headMarkup = '';
        let bodyMarkup = '';
        const trClassName = $tr.prop('class');

        if (rowIndex === 0) {
          markup += templateUtil.template(config.tblThRowTmpl, {
            trClassNames: trClassName,
            thClassNames:
              classNames.stackGroupName +
              '__headRow ' +
              classNames.stackGroupName +
              '__headRow--main',
            colSpan: 2,
            content: $tr.find('th, td').eq(0).html(),
          });
        } else {
          $tr.find('td, th').each(function (cellIndex, cell) {
            const $cell = $(cell);
            const cellHtml = $cell.html();
            const trimmedCell = cellHtml ? cellHtml.trim() : '';

            if (trimmedCell.length > 0) {
              if (cellIndex === 0) {
                headMarkup += templateUtil.template(config.tblThRowTmpl, {
                  trClassNames: trClassName,
                  thClassNames: classNames.stackGroupName + '__headRow',
                  colSpan: 2,
                  content: cellHtml,
                });
              } else {
                const keyCellMarkup = $topRow.find('td,th').eq(cellIndex).html();
                bodyMarkup += templateUtil.template(config.tblTrRowTmpl, {
                  trClassNames: trClassName,
                  groupName: classNames.stackGroupName,
                  keyContent: keyCellMarkup ? keyCellMarkup.trim() : '',
                  cellClassName: $cell.prop('class'),
                  valueContent: cellHtml,
                });
              }
            }
          });
          markup += headMarkup + bodyMarkup;
        }
      });

      base.$copy.find('tbody').before($caption).html(markup);
      base.$el.before(base.$copy);
    };

    if (mql.matches) {
      base.init();
    } else {
      mql.addListener(base.init);
    }
  };

  $.fn.svRespTables_stackTable = function () {
    return this.each(function () {
      /*jshint nonew:false*/
      new $.svRespTables.stackTable(this);
      /*jshint nonew:true*/
    });
  };

  //-Stacked Columns---------------------------------------------------------------------------//

  $.svRespTables.stackColumns = function (el) {
    var base = this,
      mql = responsiveTables.mql;

    base.$el = $(el);

    base.$el.data('svRespTables.stackColumns', base);

    base.init = function () {
      var columnCount = base.$el.find('tr').eq(0).find('td, th').length,
        columnIndex = 1, //col index starts at 0 -> start copy at second column.
        $tbody;

      mql.removeListener(base.init);

      if (columnCount < 3) {
        return;
      }

      base.$copy = $(
        templateUtil.template(config.tblTmpl, {
          classNames:
            base.$el.prop('class') +
            ' ' +
            classNames.stackGroupName +
            '__smallOnly',
        })
      );

      base.$el.addClass(classNames.stackGroupName + '__largeOnly');

      if (base.$el.find('caption').length) {
        base.$copy.find('tbody').before(base.$el.find('caption').clone());
      }

      $tbody = $('<tbody />');

      function makeTableRow(index, trEl) {
        var $tr = $(trEl),
          $newTr = $('<tr></tr>').addClass($tr.attr('class')),
          $firstCell,
          $secondCell,
          target,
          i;

        if (index === 0) {
          $newTr.addClass(
            classNames.stackGroupName +
              '__headRow ' +
              classNames.stackGroupName +
              '__headRow--main'
          );
        }

        $firstCell = $tr
          .find('td, th')
          .eq(0)
          .clone()
          .addClass(classNames.stackGroupName + '__key');

        target = columnIndex;

        // if colspan apply, recompute target for second cell.
        if ($tr.find('*[colspan]').length) {
          i = 0;
          $tr.find('td, th').each(function (j, colSpanEl) {
            var colspan = $(colSpanEl).attr('colspan');
            if (i > columnIndex) {
              //target is set; break.
              return false;
            }
            if (colspan) {
              colspan = parseInt(colspan, 10);
              target -= colspan - 1;
              if (i + colspan > columnIndex) {
                //out of current bounds
                target += i + colspan - columnIndex - 1;
              }
              i += colspan;
            } else {
              i++;
            }
          });
        }

        $secondCell = $tr.find('td, th').eq(target).clone();

        $secondCell
          .addClass(classNames.stackGroupName + '__value')
          .removeAttr('colspan');

        $newTr.append($firstCell, $secondCell);

        $tbody.append($newTr);
      }

      while (columnIndex < columnCount) {
        base.$el.find('tr').each(makeTableRow);
        ++columnIndex;
      }

      base.$copy.find('tbody').append($tbody.html());
      base.$el.before(base.$copy);
    };

    if (mql.matches) {
      base.init();
    } else {
      mql.addListener(base.init);
    }
  };

  $.fn.svRespTables_stackColumns = function () {
    return this.each(function () {
      /*jshint nonew:false*/
      new $.svRespTables.stackColumns(this);
      /*jshint nonew:true*/
    });
  };
})();

/**
 * SiteVision Responsive Tables Init
 */

responsiveTables.init = function () {
  const $responsiveTables = $(document.getElementsByTagName('table')).filter(
    selectors.groupName
  );
  if ($responsiveTables.length > 0) {
    responsiveTables.appendCSS();
    $responsiveTables
      .filter(selectors.scroll)
      .svRespTables_scroll()
      .end()
      .filter(selectors.fixedFirst)
      .svRespTables_fixedFirst()
      .end()
      .filter(selectors.stackTable)
      .svRespTables_stackTable()
      .end()
      .filter(selectors.stackColumns)
      .svRespTables_stackColumns();
  }
};

$(function () {
  const $body = $(document.body);
  // Require matchMedia support (no IE9)
  if (
    window.matchMedia &&
    $body.hasClass('sv-responsive') &&
    !$body.hasClass('sv-editing-mode')
  ) {
    responsiveTables.mql = window.matchMedia(
      '(max-width: ' + responsiveTables.config.breakpoint + ')'
    );
    responsiveTables.init();
  }
});

/**
 * SiteVision Responsive Tables CSS Stylesheet
 */

function getSheet(maxWidth) {
  const style = document.createElement('style');

  style.appendChild(document.createTextNode('')); // WebKit hack :(
  document.head.appendChild(style);
  if (maxWidth) {
    style.setAttribute('media', '(max-width: ' + maxWidth + ')');
  }
  return style.sheet;
}

const cssRules = {
  maxWidth: [
    'div.sv-responsiveTable { overflow: auto; max-width: 100%; }',
    'div.sv-responsiveTable * { white-space: nowrap !important; }',
    'div.sv-responsiveTable--fixedFirst { position: relative; margin-bottom: 20px; overflow: hidden; border-right: 1px solid var(--env-border-color); }',
    'div.sv-responsiveTable--fixedFirst table { margin-bottom: 0; overflow: visible; max-width: none; }',
    'div.sv-responsiveTable--fixedFirst td, div.sv-responsiveTable--fixedFirst th { position: relative; overflow: hidden; }',
    'div.sv-responsiveTable--fixedFirst__scrollable th:first-child, div.sv-responsiveTable--fixedFirst__scrollable td:first-child { display: none; }',
    'div.sv-responsiveTable--fixedFirst__pinned { position: absolute; left: 0; top: 0; overflow: hidden; overflow-x: scroll; border-right: 1px solid var(--env-border-color); border-left: 1px solid var(--env-border-color); }',
    'div.sv-responsiveTable--fixedFirst__pinned table { border-right: none; border-left: none; width: 100%; }',
    'div.sv-responsiveTable--fixedFirst__pinned td:first-child, div.sv-responsiveTable--fixedFirst__pinned th:first-child { width: 100%; }',
    'div.sv-responsiveTable--fixedFirst__scrollable { overflow: scroll; overflow-y: hidden; }',
    '.sv-responsiveTable--stacked { overflow: visible; }',
    '.sv-responsiveTable--stacked__largeOnly { display: none; }',
    '.sv-responsiveTable--stacked__smallOnly { display: table; }',
  ],
  noMedia: [
    '.sv-responsiveTable--stacked__largeOnly { display: table; }',
    '.sv-responsiveTable--stacked__smallOnly { display: none; }',
  ],
};

responsiveTables.appendCSS = function () {
  const sheetNoMedia = getSheet();
  const sheetMaxWidth = getSheet(responsiveTables.config.breakpoint);

  $.each(cssRules.noMedia, function (i, rule) {
    sheetNoMedia.insertRule(rule, i);
  });
  $.each(cssRules.maxWidth, function (i, rule) {
    sheetMaxWidth.insertRule(rule, i);
  });
};
